import { LitElement, css, html, nothing } from 'lit';
import { CustomEventType } from '@/events/CustomEventRegistry';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import SmallHelpers from '@/utils/SmallHelpers';
import { TeamColorOption } from '@/utils/teamColorPalette';
import './TeamColorSwatch';

@customElement('team-color-picker')
export class TeamColorPicker extends LitElement {
    static readonly styles = css`
        :host {
            position: relative;
            display: inline-block;
        }

        .trigger {
            width: 24px;
            height: 24px;
            cursor: pointer;
        }

        .trigger:hover {
            --swatch-border-color: #999;
        }

        .panel {
            background: rgba(255, 255, 255, 0.92);
            backdrop-filter: blur(15px);
            -webkit-backdrop-filter: blur(15px);
            position: absolute;
            top: calc(100% + 6px);
            left: 0;
            border-radius: 5px;
            box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.2);
            padding: 12px;
            z-index: 999;
            box-sizing: border-box;
        }

        .panel-label {
            user-select: none;
            font-size: 9pt;
            text-transform: uppercase;
            font-weight: bold;
            color: #bbb;
            margin: 0 0 8px 0;
            display: block;
            font-family: var(--font-text, 'Roboto Slab', serif);
            white-space: nowrap;
        }

        .swatches-grid {
            display: grid;
            grid-template-columns: repeat(5, 28px);
            gap: 4px;
        }

        .swatch {
            width: 28px;
            height: 28px;
            border-radius: 5px;
            cursor: pointer;
            border: 2px solid #eee;
            box-sizing: border-box;
            transition: transform linear 0.15s, border-color linear 0.15s;
        }

        .swatch:hover {
            transform: scale(1.15);
        }

        .swatch.selected {
            border-color: #333;
        }
    `;

    @property({ type: Array }) options: TeamColorOption[] = [];
    @property({ type: String }) selectedColor: string = '';

    @state() private _isOpen = false;

    private readonly _smallHelpers = new SmallHelpers();

    private readonly _handleOutsideClick = (event: MouseEvent) => {
        if (!event.composedPath().includes(this)) {
            this._close();
        }
    };

    private _open() {
        this._isOpen = true;
        setTimeout(() => {
            document.addEventListener('click', this._handleOutsideClick);
        }, 0);
    }

    private _close() {
        this._isOpen = false;
        document.removeEventListener('click', this._handleOutsideClick);
    }

    private _handleTriggerClick(event: MouseEvent) {
        event.stopPropagation();
        if (this._isOpen) {
            this._close();
        } else {
            this._open();
        }
    }

    private _handleSwatchClick(value: string) {
        this.dispatchEvent(new CustomEvent(CustomEventType.COLOR_PICKED, {
            detail: { color: value },
            bubbles: true,
            composed: true,
        }));
        this._close();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._handleOutsideClick);
    }

    render() {
        return html`
            <div class="trigger" title="Farbe auswählen" @click=${this._handleTriggerClick}>
                <team-color-swatch .color=${this.selectedColor}></team-color-swatch>
            </div>
            ${this._isOpen ? html`
                <div class="panel">
                    <span class="panel-label">Teamfarbe</span>
                    <div class="swatches-grid">
                        ${this.options.map(option => html`
                            <div
                                class="swatch ${option.value === this.selectedColor ? 'selected' : ''}"
                                style=${styleMap(this._smallHelpers.getTeamColor(option.value))}
                                title=${option.label}
                                @click=${() => this._handleSwatchClick(option.value)}
                            ></div>
                        `)}
                    </div>
                </div>
            ` : nothing}
        `;
    }
}

export default TeamColorPicker;
